import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShieldCheck, Check, X, LockKeyhole, Pencil, Trash2, ThumbsUp, BadgeCheck, Film, Upload, Zap, Rocket, Bell, BarChart2, MailWarning, Compass, Plus, AlertTriangle, CreditCard, Clock, Play } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";
import { safeUrl } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { FadeUp } from "@/components/Reveal";
import { Stars } from "@/components/SignalBadges";

const CATEGORIES = ["AI", "SEO", "Travel", "Social", "Real Estate", "Marketing", "Others"];
const REGIONS = ["US", "UK", "India", "Global"];

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#121210] px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-emerald-500/50 transition-colors";
const selectCls =
  "w-full rounded-lg border border-white/10 bg-[#121210] px-3 py-2 text-sm text-slate-100 outline-none focus:border-emerald-500/50 transition-colors";

// Tiers whose activation (leaderboard/home-screen video, Top Spot) requires a linked listing.
function subNeedsListing(sub) {
  if (sub.tier === "boost" || sub.tier === "pro_boost") return true;
  if (sub.tier === "discover_boost" && sub.option_selected === "option_b") return true;
  return false;
}

const fmtMoney = (cents, currency) =>
  cents == null ? "—" : `${(cents / 100).toFixed(2)} ${(currency || "usd").toUpperCase()}`;

// ── Unmatched Payments — payments with no listing matched ─────────
function UnmatchedPaymentsManager({ submissions, companies, busy, onLink }) {
  const [picks, setPicks] = useState({});
  if (submissions.length === 0) return null;
  return (
    <section data-testid="unmatched-payments-section" className="mt-12">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-8 h-8 rounded-lg bg-red-500/15 flex items-center justify-center">
          <MailWarning size={16} className="text-red-400" />
        </div>
        <div>
          <h2 className="font-display text-lg uppercase tracking-wide text-slate-100">Unmatched Payments</h2>
          <p className="text-xs text-slate-500">
            Paid at checkout but the Business Name / email didn't match a listing — link it or refund it.
          </p>
        </div>
        <span className="ml-auto font-mono2 text-[11px] text-red-400">{submissions.length} to resolve</span>
      </div>
      <div className="space-y-2.5">
        {submissions.map((sub) => (
          <div
            key={sub.id}
            data-testid={`unmatched-payment-${sub.id}`}
            className="rounded-xl border border-red-500/30 bg-red-500/[0.05] p-4 flex flex-wrap items-center gap-3"
          >
            <div className="min-w-[180px] flex-1">
              <p className="font-medium text-slate-200">{sub.business_name}</p>
              <p className="text-xs text-slate-500">{sub.contact_email}</p>
              <p className="font-mono2 text-[10px] uppercase tracking-widest text-emerald-400 mt-1">
                Paid {fmtMoney(sub.amount_paid, sub.currency)} · {TIER_LABELS[sub.tier] || sub.tier}
                {sub.paid_at && ` · ${new Date(sub.paid_at).toLocaleDateString()}`}
              </p>
            </div>
            <select
              data-testid={`unmatched-payment-select-${sub.id}`}
              value={picks[sub.id] || ""}
              onChange={(e) => setPicks((p) => ({ ...p, [sub.id]: e.target.value }))}
              className="rounded-lg border border-white/10 bg-[#121210] px-3 py-2 text-sm text-slate-200 outline-none focus:border-emerald-500/50 max-w-[240px]"
            >
              <option value="">Select a listing…</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <button
              data-testid={`unmatched-payment-link-${sub.id}`}
              disabled={busy === sub.id || !picks[sub.id]}
              onClick={() => onLink(sub, picks[sub.id])}
              className="inline-flex items-center gap-1 border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-bold rounded-full px-4 py-2 transition disabled:opacity-40"
            >
              <Check size={11} /> Link listing
            </button>
            {sub.stripe_url && (
              <a
                href={sub.stripe_url}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-sky-400 hover:underline"
              >
                Stripe payment →
              </a>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}


function GatePanel({ icon: Icon, title, body, action }) {
  return (
    <main className="pt-28 md:pt-16 min-h-screen flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-30" />
      <div className="relative text-center max-w-md px-4">
        <Icon size={34} className="text-amber-400 mx-auto mb-5" />
        <h1 className="font-display text-4xl uppercase tracking-wide text-slate-100 mb-3">{title}</h1>
        <p className="text-sm text-slate-400 leading-relaxed mb-8">{body}</p>
        {action}
      </div>
    </main>
  );
}

function ClaimCard({ claim, busy, onAct }) {
  const pending = claim.status === "pending";
  return (
    <div
      data-testid={`admin-claim-card-${claim.id}`}
      className="rounded-xl border border-white/[0.07] bg-[#0A0A09] p-5"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div>
          <Link
            to={`/company/${claim.company_slug}`}
            data-testid={`admin-claim-company-${claim.id}`}
            className="font-heading font-bold text-lg text-slate-100 hover:text-emerald-300 transition-colors"
          >
            {claim.company_name}
          </Link>
          <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-600 mt-1">
            {format(new Date(claim.created_at), "MMM d, yyyy · HH:mm")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            data-testid={`admin-claim-origin-${claim.id}`}
            title="Listing Origin — Admin-Seeded means you're reviewing an ownership claim; Self-Listed means you're reviewing a new self-submitted listing"
            className={`font-mono2 text-[10px] uppercase tracking-widest rounded-md px-2.5 py-1 border ${
              claim.listing_origin === "self_listed"
                ? "text-violet-300 border-violet-500/40 bg-violet-500/10"
                : "text-sky-300 border-sky-500/40 bg-sky-500/10"
            }`}
          >
            {claim.listing_origin === "self_listed" ? "Self-Listed" : "Admin-Seeded"}
          </span>
          {claim.verification_method === "otp_verified" && (
            <span
              data-testid={`admin-claim-otp-${claim.id}`}
              title="Email ownership proven by OTP"
              className="font-mono2 text-[10px] uppercase tracking-widest rounded-md px-2.5 py-1 border text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
            >
              OTP verified
            </span>
          )}
          {claim.domain_match && (
            <span
              data-testid={`admin-claim-domain-match-${claim.id}`}
              title={`Email domain matches ${claim.company_website || "the listing's website"}`}
              className="font-mono2 text-[10px] uppercase tracking-widest rounded-md px-2.5 py-1 border text-cyan-400 border-cyan-500/40 bg-cyan-500/10"
            >
              Domain match
            </span>
          )}
          <span
            data-testid={`admin-claim-status-${claim.id}`}
            className={`font-mono2 text-[10px] uppercase tracking-widest rounded-md px-2.5 py-1 border ${
              claim.status === "approved"
                ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
                : claim.status === "rejected"
                  ? "text-red-400 border-red-500/40 bg-red-500/10"
                  : "text-amber-400 border-amber-500/40 bg-amber-500/10"
            }`}
          >
            {claim.status}
          </span>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm mb-3">
        <p className="text-slate-400">
          <span className="font-mono2 text-[10px] uppercase tracking-widest text-slate-600 block">Name</span>
          {claim.name}
        </p>
        <p className="text-slate-400">
          <span className="font-mono2 text-[10px] uppercase tracking-widest text-slate-600 block">Role</span>
          {claim.role}
        </p>
        <p className="text-slate-400">
          <span className="font-mono2 text-[10px] uppercase tracking-widest text-slate-600 block">Business email</span>
          {claim.business_email}
        </p>
        <p className="text-slate-400 break-all">
          <span className="font-mono2 text-[10px] uppercase tracking-widest text-slate-600 block">Website</span>
          {claim.website || "—"}
        </p>
      </div>
      <p className="text-sm text-slate-500 leading-relaxed rounded-lg bg-[#121210] border border-white/5 px-4 py-3 mb-4">
        {claim.message}
      </p>
      {pending && (
        <div className="flex gap-3">
          <button
            data-testid={`approve-claim-button-${claim.id}`}
            disabled={busy === claim.id}
            onClick={() => onAct(claim, "approve")}
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-bold text-sm rounded-full px-5 py-2 transition-colors duration-200"
          >
            <Check size={15} /> Approve — mark Claimed
          </button>
          <button
            data-testid={`reject-claim-button-${claim.id}`}
            disabled={busy === claim.id}
            onClick={() => onAct(claim, "reject")}
            className="inline-flex items-center gap-2 border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 disabled:opacity-50 text-red-400 font-bold text-sm rounded-full px-5 py-2 transition-colors duration-200"
          >
            <X size={15} /> Reject
          </button>
        </div>
      )}
    </div>
  );
}

function GifQueue({ gifs, reasons, setReasons, busy, onApprove, onReject }) {
  const items = gifs.flatMap((g) => {
    const media = [];
    if (["pending_approval", "rejected"].includes(g.gif_status)) {
      media.push({
        ...g,
        media_type: "gif",
        media_label: "Generated GIF",
        media_url: g.gif_url,
        media_status: g.gif_status,
        reject_reason: g.gif_reject_reason,
      });
    }
    if (["pending_approval", "rejected"].includes(g.slot_video_status)) {
      media.push({
        ...g,
        media_type: "video",
        media_label: "Uploaded video",
        media_url: g.slot_video_url,
        media_webm: g.slot_video_webm,
        media_status: g.slot_video_status,
        reject_reason: g.slot_video_reject_reason,
      });
    }
    return media;
  });
  const pendingCount = items.filter((item) => item.media_status === "pending_approval").length;

  return (
    <div>
      <h2 className="font-heading text-xl font-bold text-slate-100 mb-5">
        Top 3 media queue <span className="font-mono2 text-sm text-amber-400">({pendingCount} pending)</span>
      </h2>
      <div className="space-y-3" data-testid="admin-media-queue">
        {items.length === 0 && (
          <div className="rounded-xl border border-white/[0.07] bg-[#0A0A09] p-8 text-center">
            <p className="text-sm text-slate-500">No GIFs or videos waiting for review.</p>
          </div>
        )}
        {items.map((item) => {
          const itemKey = `${item.id}-${item.media_type}`;
          return (
            <div
              key={itemKey}
              data-testid={`media-queue-card-${itemKey}`}
              className="rounded-xl border border-white/[0.07] bg-[#0A0A09] p-5 flex flex-wrap items-center gap-5"
            >
              <div className="w-40 aspect-video rounded-lg overflow-hidden border border-amber-500/30 bg-black shrink-0">
                {item.media_type === "video" && item.media_url ? (
                  <video
                    data-testid={`media-preview-${itemKey}`}
                    controls
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    className="w-full h-full object-cover"
                  >
                    {item.media_webm && <source src={item.media_webm} type="video/webm" />}
                    <source src={item.media_url} type="video/mp4" />
                  </video>
                ) : item.media_url ? (
                  <img
                    src={item.media_url}
                    alt={`${item.name} ${item.media_label}`}
                    data-testid={`media-preview-${itemKey}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-600 text-xs">no file</div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-heading font-bold text-slate-100">{item.name}</p>
                  <span className="font-mono2 text-[10px] uppercase tracking-widest text-amber-400 border border-amber-500/40 bg-amber-500/10 rounded px-2 py-0.5 inline-flex items-center gap-1">
                    {item.media_type === "video" ? <Film size={10} /> : null} {item.media_label}
                  </span>
                </div>
                <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-600 mt-1">
                  {item.media_type === "gif" ? `${item.gif_regen_count}/3 generations used` : "Manual owner upload · 3–15 seconds"}
                </p>
                {item.media_status === "rejected" && item.reject_reason && (
                  <p className="text-xs text-red-400 mt-1.5">Rejected: {item.reject_reason}</p>
                )}
                {item.media_status === "pending_approval" && (
                  <input
                    data-testid={`media-reject-reason-${itemKey}`}
                    value={reasons[itemKey] || ""}
                    onChange={(e) => setReasons((r) => ({ ...r, [itemKey]: e.target.value }))}
                    placeholder="Reject reason (optional)"
                    className={`${inputCls} mt-2.5 max-w-sm`}
                  />
                )}
              </div>
              {item.media_status === "pending_approval" && (
                <div className="flex gap-2.5 shrink-0">
                  <button
                    data-testid={`media-approve-${itemKey}`}
                    disabled={busy === itemKey}
                    onClick={() => onApprove(item)}
                    className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-bold text-xs rounded-full px-4 py-2 transition-colors"
                  >
                    <Check size={13} /> Approve — go live
                  </button>
                  <button
                    data-testid={`media-reject-${itemKey}`}
                    disabled={busy === itemKey}
                    onClick={() => onReject(item)}
                    className="inline-flex items-center gap-1.5 border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 disabled:opacity-50 text-red-400 font-bold text-xs rounded-full px-4 py-2 transition-colors"
                  >
                    <X size={13} /> Reject
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function Top3Manager({ data, busy, onAssign, onRemove }) {
  const [category, setCategory] = useState("");
  const [region, setRegion] = useState("");
  const categories = ["AI", "SEO", "Travel", "Social", "Real Estate", "Marketing", "Others"];
  const regions = ["US", "UK", "India", "Global"];
  const eligible = data?.eligible || [];
  const filtered = eligible.filter(
    (company) => (!category || company.category === category) && (!region || company.region === region),
  );

  return (
    <div data-testid="admin-top3-manager">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <h2 className="font-heading text-xl font-bold text-slate-100">Manual Top 3</h2>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl leading-relaxed">
            Only Claimed + Verified businesses can hold these global slots. HighRank Score is shown as a suggestion,
            not an automatic ranking rule.
          </p>
        </div>
        <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-emerald-400 border border-emerald-500/30 bg-emerald-500/10 rounded-full px-3 py-1">
          {eligible.length} eligible
        </span>
      </div>

      <div className="grid md:grid-cols-3 gap-3 mb-6">
        {[1, 2, 3].map((position) => {
          const slot = data?.slots?.[position - 1];
          return (
            <div
              key={position}
              data-testid={`top3-slot-${position}`}
              className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.04] p-4"
            >
              <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-emerald-400 mb-3">
                Top 3 · Position #{position}
              </p>
              <select
                data-testid={`top3-slot-select-${position}`}
                value={slot?.id || ""}
                disabled={busy === `top3-${position}`}
                onChange={(e) => (e.target.value ? onAssign(e.target.value, position) : slot && onRemove(slot.id))}
                className={selectCls}
              >
                <option value="">Open slot</option>
                {eligible.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}{company.top3_position ? ` — currently #${company.top3_position}` : ""}
                  </option>
                ))}
              </select>
              <div className="mt-3 min-h-10">
                {slot ? (
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-heading font-semibold text-sm text-slate-100">{slot.name}</p>
                      <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">
                        Score {slot.highrank_score ?? "Not yet scored"}
                      </p>
                    </div>
                    <button
                      data-testid={`top3-remove-${position}`}
                      onClick={() => onRemove(slot.id)}
                      disabled={busy === `top3-${position}`}
                      className="text-red-400 hover:text-red-300 text-xs font-bold disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-slate-600">No business assigned.</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="rounded-xl border border-white/[0.07] bg-[#0A0A09] p-4">
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-600">Eligible businesses</p>
          <select
            data-testid="top3-filter-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className={`${selectCls} max-w-44`}
          >
            <option value="">All categories</option>
            {categories.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          <select
            data-testid="top3-filter-region"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className={`${selectCls} max-w-36`}
          >
            <option value="">All regions</option>
            {regions.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </div>
        <div className="space-y-2" data-testid="top3-eligible-list">
          {filtered.length === 0 && (
            <p className="text-sm text-slate-500 py-4 text-center">No Claimed + Verified businesses match this filter.</p>
          )}
          {filtered.map((company) => (
            <div
              key={company.id}
              data-testid={`top3-eligible-${company.slug}`}
              className="flex items-center gap-3 flex-wrap rounded-lg border border-white/5 px-3 py-2.5"
            >
              <p className="font-heading font-semibold text-sm text-slate-100">{company.name}</p>
              <span className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">
                {company.category} · {company.region}
              </span>
              <span className="font-mono2 text-[10px] uppercase tracking-widest text-emerald-400">
                Score {company.highrank_score ?? "Not yet scored"}
              </span>
              {company.top3_position && (
                <span className="font-mono2 text-[10px] uppercase tracking-widest text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded px-2 py-0.5">
                  Top 3 #{company.top3_position}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function QuickAddBusiness({ busy, onAdd }) {
  const [form, setForm] = useState({ business_name: "", website: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const ok = await onAdd(form);
    if (ok) setForm({ business_name: "", website: "" });
  };

  return (
    <div className="rounded-xl border border-white/[0.07] bg-[#0A0A09] p-5 mb-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center">
          <Plus size={16} className="text-emerald-400" />
        </div>
        <div>
          <h2 className="font-display text-lg uppercase tracking-wide text-slate-100">Quick-Add Business</h2>
          <p className="text-xs text-slate-500">
            Name + website only — lists it unclaimed on the leaderboard. When the real owner claims it with a
            matching email domain, it'll appear in Business Claims for your approval.
          </p>
        </div>
      </div>
      <form onSubmit={submit} data-testid="quick-add-business-form" className="grid sm:grid-cols-[1fr_1fr_auto] gap-3">
        <input
          data-testid="quick-add-business-name-input"
          value={form.business_name}
          onChange={set("business_name")}
          required
          minLength={2}
          placeholder="Business name"
          className={inputCls}
        />
        <input
          data-testid="quick-add-business-website-input"
          value={form.website}
          onChange={set("website")}
          required
          minLength={3}
          type="url"
          placeholder="https://business-website.com"
          className={inputCls}
        />
        <button
          type="submit"
          data-testid="quick-add-business-submit-button"
          disabled={busy === "quick-add"}
          className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-bold text-sm rounded-full px-5 py-2 transition-colors duration-200 whitespace-nowrap"
        >
          <Plus size={14} /> {busy === "quick-add" ? "Adding…" : "Add"}
        </button>
      </form>
    </div>
  );
}

const ORIGIN_LABEL = { admin_seeded: "Admin-Seeded", self_listed: "Self-Listed" };
const OWNERSHIP_LABEL = { unclaimed: "Unclaimed", claim_pending: "Claim Pending", claimed_verified: "Claimed & Verified" };
const OWNERSHIP_CLS = {
  unclaimed: "text-slate-400 border-white/15 bg-white/[0.04]",
  claim_pending: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  claimed_verified: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
};

function OwnershipTags({ company }) {
  const origin = company.listing_origin || "admin_seeded";
  const status = company.ownership_status || "unclaimed";
  return (
    <>
      <span
        data-testid={`listing-origin-${company.id}`}
        title="Listing Origin"
        className={`font-mono2 text-[10px] uppercase tracking-widest rounded px-2 py-0.5 border inline-flex items-center gap-1 ${
          origin === "self_listed" ? "text-violet-300 border-violet-500/40 bg-violet-500/10" : "text-sky-300 border-sky-500/40 bg-sky-500/10"
        }`}
      >
        {ORIGIN_LABEL[origin]}
      </span>
      <span
        data-testid={`ownership-status-${company.id}`}
        title="Ownership Status"
        className={`font-mono2 text-[10px] uppercase tracking-widest rounded px-2 py-0.5 border inline-flex items-center gap-1 ${OWNERSHIP_CLS[status]}`}
      >
        {status === "claimed_verified" && <BadgeCheck size={11} />}
        {OWNERSHIP_LABEL[status]}
      </span>
    </>
  );
}

function ListingsManager({ companies, editing, setEditing, editForm, setEditForm, busy, onVerify, onSave, onDelete, onRemoveMedia, onUploadMedia, onToggleLandingVideo }) {
  const [originFilter, setOriginFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("rank");

  const visible = companies
    .filter((c) => originFilter === "all" || (c.listing_origin || "admin_seeded") === originFilter)
    .filter((c) => statusFilter === "all" || (c.ownership_status || "unclaimed") === statusFilter)
    .slice()
    .sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "origin") return (a.listing_origin || "").localeCompare(b.listing_origin || "");
      if (sortBy === "status") return (a.ownership_status || "").localeCompare(b.ownership_status || "");
      return (a.rank_position || 9999) - (b.rank_position || 9999);
    });

  const selectCls = "rounded-lg border border-white/10 bg-[#121210] px-3 py-1.5 font-mono2 text-[11px] uppercase tracking-widest text-slate-300 outline-none focus:border-emerald-500/50";

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h2 className="font-heading text-xl font-bold text-slate-100">
          Listings <span className="font-mono2 text-sm text-slate-500">({visible.length}/{companies.length})</span>
        </h2>
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          <select data-testid="listings-filter-origin" value={originFilter} onChange={(e) => setOriginFilter(e.target.value)} className={selectCls}>
            <option value="all">Origin: All</option>
            <option value="admin_seeded">Admin-Seeded</option>
            <option value="self_listed">Self-Listed</option>
          </select>
          <select data-testid="listings-filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selectCls}>
            <option value="all">Status: All</option>
            <option value="unclaimed">Unclaimed</option>
            <option value="claim_pending">Claim Pending</option>
            <option value="claimed_verified">Claimed &amp; Verified</option>
          </select>
          <select data-testid="listings-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={selectCls}>
            <option value="rank">Sort: Rank</option>
            <option value="name">Sort: Name</option>
            <option value="status">Sort: Ownership</option>
            <option value="origin">Sort: Origin</option>
          </select>
        </div>
      </div>
      <div className="space-y-2.5" data-testid="admin-listings">
        {visible.length === 0 && (
          <p data-testid="admin-listings-empty" className="text-sm text-slate-500 py-6 text-center">No listings match these filters.</p>
        )}
        {visible.map((c) => (
          <div key={c.id} className="rounded-xl border border-white/[0.07] bg-[#0A0A09] p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <span data-testid={`admin-rank-${c.id}`} className="font-mono2 text-xs font-bold text-emerald-400 w-8">#{c.rank_position}</span>
              <Link
                to={`/company/${c.slug}`}
                className="font-heading font-semibold text-slate-100 text-sm hover:text-emerald-300 transition-colors"
              >
                {c.name}
              </Link>
              <span className="font-mono2 text-[10px] uppercase tracking-widest text-slate-600">
                {c.category} · {c.region}
              </span>
              <OwnershipTags company={c} />
              {c.verified && (
                <span className="font-mono2 text-[10px] uppercase tracking-widest text-cyan-400 border border-cyan-500/40 bg-cyan-500/10 rounded px-2 py-0.5 inline-flex items-center gap-1">
                  <ShieldCheck size={11} /> Verified
                </span>
              )}
              {!c.claimed && !c.contact_email && (
                <span
                  data-testid={`no-email-badge-${c.id}`}
                  title="No contact email — OTP claim verification unavailable"
                  className="font-mono2 text-[10px] uppercase tracking-widest text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded px-2 py-0.5 inline-flex items-center gap-1"
                >
                  <MailWarning size={11} /> No email
                </span>
              )}
              <div className="flex gap-2 ml-auto">
                <button
                  data-testid={`verify-toggle-${c.id}`}
                  disabled={busy === c.id}
                  onClick={() => onVerify(c)}
                  title="Manual verification — independent of Claimed status"
                  className={`inline-flex items-center gap-1.5 font-bold text-xs rounded-full px-3.5 py-1.5 border transition-colors disabled:opacity-50 ${
                    c.verified
                      ? "border-cyan-500/50 bg-cyan-500/15 text-cyan-400 hover:bg-cyan-500/25"
                      : "border-white/15 text-slate-400 hover:text-cyan-400 hover:border-cyan-500/40"
                  }`}
                >
                  <ShieldCheck size={12} /> {c.verified ? "Verified" : "Verify"}
                </button>
                <button
                  data-testid={`edit-company-${c.id}`}
                  onClick={() => {
                    setEditing(editing === c.id ? null : c.id);
                    setEditForm({
                      name: c.name, tagline: c.tagline, website: c.website,
                      description: c.description, category: c.category, region: c.region,
                      contact_email: c.contact_email || "",
                    });
                  }}
                  className="inline-flex items-center gap-1.5 border border-white/15 text-slate-400 hover:text-emerald-400 hover:border-emerald-500/40 font-bold text-xs rounded-full px-3.5 py-1.5 transition-colors"
                >
                  <Pencil size={12} /> Edit
                </button>
                <button
                  data-testid={`delete-company-${c.id}`}
                  disabled={busy === c.id}
                  onClick={() => onDelete(c)}
                  className="inline-flex items-center gap-1.5 border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 disabled:opacity-50 text-red-400 font-bold text-xs rounded-full px-3.5 py-1.5 transition-colors"
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
            {(c.verified || c.logo_url || c.gif_url || c.slot_video_url || c.slot_video_webm) && (
              <div
                data-testid={`media-controls-${c.id}`}
                className="mt-3 pt-3 border-t border-white/5 flex items-center gap-2 flex-wrap"
              >
                <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-600 mr-1">Admin media</span>
                {c.verified && (
                  <>
                    <input
                      id={`admin-logo-upload-${c.id}`}
                      data-testid={`admin-logo-upload-input-${c.id}`}
                      type="file"
                      accept=".png,.svg,.jpg,.jpeg"
                      className="hidden"
                      disabled={busy === `upload-${c.id}-logo`}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) onUploadMedia(c, "logo", file);
                        e.target.value = "";
                      }}
                    />
                    <label
                      htmlFor={`admin-logo-upload-${c.id}`}
                      data-testid={`admin-upload-logo-${c.id}`}
                      className={`inline-flex items-center gap-1 border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 font-bold text-[11px] rounded-full px-3 py-1 transition-colors cursor-pointer ${busy === `upload-${c.id}-logo` ? "opacity-50 pointer-events-none" : ""}`}
                    >
                      <Upload size={11} /> {busy === `upload-${c.id}-logo` ? "Uploading…" : c.logo_url ? "Replace logo" : "Upload logo"}
                    </label>
                  </>
                )}
                {c.verified && (
                  <>
                    {c.rank_position <= 10 ? (
                      <>
                        <input
                          id={`admin-video-upload-${c.id}`}
                          data-testid={`admin-video-upload-input-${c.id}`}
                          type="file"
                          accept=".mp4,.webm,.mov,video/mp4,video/webm,video/quicktime"
                          className="hidden"
                          disabled={busy === `upload-${c.id}-video`}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) onUploadMedia(c, "video", file);
                            e.target.value = "";
                          }}
                        />
                        <label
                          htmlFor={`admin-video-upload-${c.id}`}
                          data-testid={`admin-upload-video-${c.id}`}
                          className={`inline-flex items-center gap-1 border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 font-bold text-[11px] rounded-full px-3 py-1 transition-colors cursor-pointer ${busy === `upload-${c.id}-video` ? "opacity-50 pointer-events-none" : ""}`}
                        >
                          <Film size={11} /> {busy === `upload-${c.id}-video` ? "Uploading…" : c.slot_video_url ? "Replace video" : "Upload video"}
                        </label>
                      </>
                    ) : (
                      <span
                        data-testid={`admin-upload-video-disabled-${c.id}`}
                        title={`Video upload is only available for the top 10 ranked listings (this listing is #${c.rank_position})`}
                        className="inline-flex items-center gap-1 border border-white/10 bg-white/5 text-slate-600 font-bold text-[11px] rounded-full px-3 py-1 cursor-not-allowed select-none"
                      >
                        <Film size={11} /> Video (top 10 only)
                      </span>
                    )}
                  </>
                )}
                {c.logo_url && (
                  <button
                    data-testid={`remove-logo-${c.id}`}
                    disabled={busy === `${c.id}-logo`}
                    onClick={() => onRemoveMedia(c, "logo")}
                    className="inline-flex items-center gap-1 border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 disabled:opacity-50 text-red-400 font-bold text-[11px] rounded-full px-3 py-1 transition-colors"
                  >
                    <Trash2 size={11} /> Remove logo
                  </button>
                )}
                {c.gif_url && (
                  <button
                    data-testid={`remove-gif-${c.id}`}
                    disabled={busy === `${c.id}-gif`}
                    onClick={() => onRemoveMedia(c, "gif")}
                    className="inline-flex items-center gap-1 border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 disabled:opacity-50 text-red-400 font-bold text-[11px] rounded-full px-3 py-1 transition-colors"
                  >
                    <Trash2 size={11} /> Remove GIF ({c.gif_status})
                  </button>
                )}
                {(c.slot_video_url || c.slot_video_webm) && (
                  <button
                    data-testid={`remove-video-${c.id}`}
                    disabled={busy === `${c.id}-video`}
                    onClick={() => onRemoveMedia(c, "video")}
                    className="inline-flex items-center gap-1 border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 disabled:opacity-50 text-red-400 font-bold text-[11px] rounded-full px-3 py-1 transition-colors"
                  >
                    <Trash2 size={11} /> Remove video ({c.slot_video_status})
                  </button>
                )}
                {(c.slot_video_status === "approved" || c.home_screen_video) && (
                  <button
                    data-testid={`toggle-landing-video-${c.id}`}
                    disabled={busy === `${c.id}-landing-video`}
                    onClick={() => onToggleLandingVideo(c, !c.home_screen_video)}
                    title={c.home_screen_video ? "Remove this video from the home page video sequence" : "Feature this leaderboard video on the home page video sequence"}
                    className={`inline-flex items-center gap-1 border font-bold text-[11px] rounded-full px-3 py-1 transition-colors disabled:opacity-50 ${
                      c.home_screen_video
                        ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25"
                        : "border-white/15 bg-white/5 text-slate-400 hover:text-emerald-400 hover:border-emerald-500/40"
                    }`}
                  >
                    <Play size={11} />
                    {busy === `${c.id}-landing-video` ? "Updating…" : c.home_screen_video ? `On landing page${c.home_video_manual ? "" : " (Pro)"}` : "Feature on landing page"}
                  </button>
                )}
              </div>
            )}
            {editing === c.id && (
              <div className="mt-4 pt-4 border-t border-white/5 grid sm:grid-cols-2 gap-3" data-testid={`edit-form-${c.id}`}>
                <input className={inputCls} value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} placeholder="Name" data-testid={`edit-name-${c.id}`} />
                <input className={inputCls} value={editForm.tagline} onChange={(e) => setEditForm({ ...editForm, tagline: e.target.value })} placeholder="Tagline" />
                <select className={inputCls} value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}>
                  {CATEGORIES.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
                <select className={inputCls} value={editForm.region} onChange={(e) => setEditForm({ ...editForm, region: e.target.value })}>
                  {REGIONS.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
                <input className={inputCls} value={editForm.website} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} placeholder="Website" />
                <input
                  data-testid={`edit-contact-email-${c.id}`}
                  type="email"
                  className={inputCls}
                  value={editForm.contact_email || ""}
                  onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })}
                  placeholder="Contact email (enables OTP claim verification)"
                />
                <div className="flex gap-2">
                  <button
                    data-testid={`edit-save-${c.id}`}
                    disabled={busy === c.id}
                    onClick={() => onSave(c)}
                    className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-bold text-xs rounded-full px-5 py-2 transition-colors"
                  >
                    Save
                  </button>
                  <button onClick={() => setEditing(null)} className="text-slate-500 hover:text-slate-300 text-xs font-semibold px-3">
                    Cancel
                  </button>
                </div>
                <textarea
                  className={`${inputCls} sm:col-span-2 resize-none`}
                  rows={2}
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  placeholder="Description"
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function VideosCleanup({ videos, busy, onDelete }) {
  const totalMb = videos.reduce((sum, v) => sum + (v.size || 0), 0) / 1024 / 1024;
  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-8 h-8 rounded-lg bg-slate-500/15 flex items-center justify-center">
          <Film size={16} className="text-slate-400" />
        </div>
        <div>
          <h2 className="font-display text-lg uppercase tracking-wide text-slate-100">Uploaded Videos</h2>
          <p className="text-xs text-slate-500">Every video file in storage — delete any of them.</p>
        </div>
        <div className="ml-auto text-right">
          <p data-testid="admin-videos-total-storage" className="font-mono2 text-sm text-slate-300 font-bold">
            {totalMb.toFixed(1)} MB
          </p>
          <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">
            {videos.length} total
            {videos.filter((v) => v.is_stale).length > 0 && (
              <span data-testid="admin-videos-stale-count" className="text-red-400"> · {videos.filter((v) => v.is_stale).length} expired</span>
            )}
          </p>
        </div>
      </div>
      <div className="space-y-2" data-testid="admin-videos-cleanup-list">
        {videos.length === 0 ? (
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 text-center text-sm text-slate-600">
            No uploaded videos.
          </div>
        ) : videos.map((v) => (
          <div
            key={v.id}
            data-testid={`admin-video-row-${v.id}`}
            className={`flex items-center gap-4 rounded-xl border p-4 ${
              v.is_stale
                ? "border-red-500/40 bg-red-500/[0.07]"
                : v.is_expired
                ? "border-amber-500/40 bg-amber-500/[0.06]"
                : "border-white/[0.07] bg-[#0A0A09]"
            }`}
          >
            <video src={v.url} muted className="w-24 h-14 rounded-lg object-cover bg-black shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-slate-300 truncate">
                {v.company_name ? (
                  <Link to={`/company/${v.company_slug}`} className="hover:text-emerald-300 transition-colors">{v.company_name}</Link>
                ) : (
                  <span className="text-slate-500">Unattached</span>
                )}
              </p>
              <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-600 mt-0.5">
                {(v.size / 1024 / 1024).toFixed(1)} MB · {v.created_at && format(new Date(v.created_at), "MMM d, yyyy")}
              </p>
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                {v.is_stale && (
                  <span
                    data-testid={`admin-video-stale-${v.id}`}
                    className="inline-flex items-center gap-1 rounded-full border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-[10px] font-semibold text-red-400"
                  >
                    <AlertTriangle size={10} /> Expired & unused — safe to delete
                  </span>
                )}
                {!v.is_stale && v.is_expired && (
                  <span
                    data-testid={`admin-video-expired-${v.id}`}
                    className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold text-amber-400"
                  >
                    <AlertTriangle size={10} /> Boost expired
                  </span>
                )}
                {v.in_use && !v.is_expired && (
                  <span
                    data-testid={`admin-video-live-${v.id}`}
                    className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-400"
                  >
                    Live on Top Spot / Leaderboard
                  </span>
                )}
              </div>
            </div>
            <button
              data-testid={`admin-video-delete-button-${v.id}`}
              disabled={busy === v.id}
              onClick={() => onDelete(v)}
              className="inline-flex items-center gap-1.5 border border-red-500/30 text-red-400 hover:bg-red-500/10 disabled:opacity-50 text-sm font-bold rounded-full px-4 py-2 transition-colors duration-200 shrink-0"
            >
              <Trash2 size={13} /> Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function UsersCleanup({ users, busy, onDelete }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-8 h-8 rounded-lg bg-slate-500/15 flex items-center justify-center">
          <ThumbsUp size={16} className="text-slate-400 rotate-180" />
        </div>
        <div>
          <h2 className="font-display text-lg uppercase tracking-wide text-slate-100">Registered Users</h2>
          <p className="text-xs text-slate-500">Every account on HighRank — delete any non-admin user.</p>
        </div>
        <span className="ml-auto font-mono2 text-[11px] text-slate-500">{users.length} total</span>
      </div>
      <div className="space-y-2" data-testid="admin-users-cleanup-list">
        {users.length === 0 ? (
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 text-center text-sm text-slate-600">
            No registered users.
          </div>
        ) : users.map((u) => (
          <div
            key={u.id}
            data-testid={`admin-user-row-${u.id}`}
            className="flex items-center gap-4 rounded-xl border border-white/[0.07] bg-[#0A0A09] p-4"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-bold text-slate-200 truncate">{u.name || "—"}</p>
                {u.role === "admin" && (
                  <span className="font-mono2 text-[9px] uppercase tracking-widest text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded px-1.5 py-0.5">Admin</span>
                )}
                {u.claimed_company_name && (
                  <Link
                    to={`/company/${u.claimed_company_slug}`}
                    className="font-mono2 text-[9px] uppercase tracking-widest text-emerald-400 border border-emerald-500/30 bg-emerald-500/10 rounded px-1.5 py-0.5 hover:bg-emerald-500/20 transition-colors"
                  >
                    Owns {u.claimed_company_name}
                  </Link>
                )}
              </div>
              <p className="text-xs text-slate-500 truncate mt-0.5">{u.email}</p>
              <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-600 mt-0.5">
                Joined {u.created_at && format(new Date(u.created_at), "MMM d, yyyy")}
              </p>
            </div>
            {u.role !== "admin" && (
              <button
                data-testid={`admin-user-delete-button-${u.id}`}
                disabled={busy === u.id}
                onClick={() => onDelete(u)}
                className="inline-flex items-center gap-1.5 border border-red-500/30 text-red-400 hover:bg-red-500/10 disabled:opacity-50 text-sm font-bold rounded-full px-4 py-2 transition-colors duration-200 shrink-0"
              >
                <Trash2 size={13} /> Delete
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function EndorsementsModeration({ endorsements, busy, onRemove }) {
  return (
    <div>
      <h2 className="font-heading text-xl font-bold text-slate-100 mb-5">
        Recent endorsements <span className="font-mono2 text-sm text-slate-500">({endorsements.length})</span>
      </h2>
      <div className="space-y-2.5" data-testid="admin-endorsements">
        {endorsements.length === 0 && (
          <div className="rounded-xl border border-white/[0.07] bg-[#0A0A09] p-8 text-center">
            <p className="text-sm text-slate-500">No endorsements yet.</p>
          </div>
        )}
        {endorsements.map((e) => (
          <div key={e.id} className="rounded-xl border border-white/[0.07] bg-[#0A0A09] px-4 py-3 flex items-center gap-3 flex-wrap">
            <ThumbsUp size={13} className="text-emerald-400 shrink-0" />
            <p className="text-sm text-slate-300">
              <span className="font-semibold">{e.user_name}</span>{" "}
              <span className="text-slate-500 font-mono2 text-xs">{e.user_email}</span>
            </p>
            <p className="text-xs text-slate-500">endorsed</p>
            <p className="text-sm font-semibold text-slate-100">{e.company_name}</p>
            <p className="font-mono2 text-[10px] text-slate-600 ml-auto">
              {format(new Date(e.created_at), "MMM d, HH:mm")}
            </p>
            <button
              data-testid={`remove-endorsement-${e.id}`}
              disabled={busy === e.id}
              onClick={() => onRemove(e)}
              className="inline-flex items-center gap-1 border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 disabled:opacity-50 text-red-400 font-bold text-[11px] rounded-full px-3 py-1 transition-colors"
            >
              <Trash2 size={11} /> Remove
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewsModeration({ reviews, busy, onRemove }) {
  return (
    <div>
      <h2 className="font-heading text-xl font-bold text-slate-100 mb-5">
        Recent reviews <span className="font-mono2 text-sm text-slate-500">({reviews.length})</span>
      </h2>
      <div className="space-y-2.5" data-testid="admin-reviews">
        {reviews.length === 0 && (
          <div className="rounded-xl border border-white/[0.07] bg-[#0A0A09] p-8 text-center">
            <p className="text-sm text-slate-500">No written reviews yet.</p>
          </div>
        )}
        {reviews.map((review) => (
          <div key={review.id} className="rounded-xl border border-white/[0.07] bg-[#0A0A09] px-4 py-3">
            <div className="flex items-center gap-3 flex-wrap">
              <Stars value={review.rating} size={11} testId={`admin-review-stars-${review.id}`} />
              <p className="text-sm text-slate-300">
                <span className="font-semibold">{review.user_name}</span>{" "}
                <span className="text-slate-500 font-mono2 text-xs">{review.user_email}</span>
              </p>
              <p className="text-xs text-slate-500">reviewed</p>
              <p className="text-sm font-semibold text-slate-100">{review.company_name}</p>
              <p className="font-mono2 text-[10px] text-slate-600 ml-auto">
                {format(new Date(review.created_at), "MMM d, HH:mm")}
              </p>
              <button
                data-testid={`remove-review-${review.id}`}
                disabled={busy === review.id}
                onClick={() => onRemove(review)}
                className="inline-flex items-center gap-1 border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 disabled:opacity-50 text-red-400 font-bold text-[11px] rounded-full px-3 py-1 transition-colors"
              >
                <Trash2 size={11} /> Remove
              </button>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed mt-2">{review.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function BoostRequestsManager({ boostRequests, busy, onApprove, onReject, onDeactivate }) {
  const pending = (boostRequests || []).filter((c) => c.boost_status === "pending");
  const active = (boostRequests || []).filter((c) => c.boost_status === "active");
  const rejected = (boostRequests || []).filter((c) => c.boost_status === "rejected");

  const statusBadge = (status) => {
    const map = {
      pending: "text-amber-400 border-amber-500/40 bg-amber-500/10",
      active: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
      rejected: "text-red-400 border-red-500/40 bg-red-500/10",
    };
    return (
      <span className={`font-mono2 text-[9px] uppercase tracking-widest rounded-md px-2.5 py-1 border ${map[status] || ""}`}>
        {status}
      </span>
    );
  };

  const BoostCard = ({ c }) => (
    <div data-testid={`admin-boost-card-${c.id}`} className="rounded-xl border border-white/[0.07] bg-[#0A0A09] p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div>
          <Link to={`/company/${c.slug}`} className="font-heading font-bold text-lg text-slate-100 hover:text-amber-300 transition-colors">
            {c.name}
          </Link>
          <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-600 mt-1">
            {c.category} · {c.region}
            {c.boost_requested_at && ` · Requested ${format(new Date(c.boost_requested_at), "MMM d, yyyy · HH:mm")}`}
          </p>
          {c.payment_amount != null && (
            <p className="font-mono2 text-[10px] text-emerald-400 mt-0.5">
              Paid: ${(c.payment_amount / 100).toFixed(2)} {c.payment_currency?.toUpperCase()} ·{" "}
              <span className={c.boost_tier === "prime" ? "text-amber-400" : "text-slate-400"}>
                {c.boost_tier === "prime" ? "Prime" : "Standard"}
              </span>
            </p>
          )}
          {c.boost_expires_at && (
            <p className="font-mono2 text-[10px] text-slate-500 mt-0.5">
              Expires: {format(new Date(c.boost_expires_at), "MMM d, yyyy · HH:mm")}
            </p>
          )}
        </div>
        {statusBadge(c.boost_status)}
      </div>
      <div className="flex gap-2 flex-wrap">
        {c.boost_status === "pending" && (
          <>
            <button
              data-testid={`admin-boost-approve-${c.id}`}
              disabled={busy === `boost-${c.id}`}
              onClick={() => onApprove(c)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 px-4 py-2 text-xs font-bold text-emerald-400 transition-colors disabled:opacity-50"
            >
              <Check size={13} /> Approve
            </button>
            <button
              data-testid={`admin-boost-reject-${c.id}`}
              disabled={busy === `boost-${c.id}`}
              onClick={() => onReject(c)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 px-4 py-2 text-xs font-bold text-red-400 transition-colors disabled:opacity-50"
            >
              <X size={13} /> Reject
            </button>
          </>
        )}
        {c.boost_status === "active" && (
          <button
            data-testid={`admin-boost-deactivate-${c.id}`}
            disabled={busy === `boost-${c.id}`}
            onClick={() => onDeactivate(c)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 hover:border-white/25 px-4 py-2 text-xs font-bold text-slate-400 transition-colors disabled:opacity-50"
          >
            <X size={13} /> Deactivate
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <h2 className="font-heading text-xl font-bold text-slate-100 mb-5 flex items-center gap-2">
        <Zap size={18} className="text-amber-400" />
        Boost Requests{" "}
        <span className="font-mono2 text-sm text-amber-400">({pending.length} pending)</span>
      </h2>

      {pending.length === 0 && active.length === 0 && rejected.length === 0 && (
        <div className="rounded-xl border border-white/[0.07] bg-[#0A0A09] p-8 text-center">
          <p className="text-sm text-slate-500">No boost requests yet.</p>
        </div>
      )}

      {pending.length > 0 && (
        <div className="space-y-3 mb-4" data-testid="admin-boost-pending">
          {pending.map((c) => <BoostCard key={c.id} c={c} />)}
        </div>
      )}

      {active.length > 0 && (
        <>
          <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500 mb-2 mt-5">Active boosts</p>
          <div className="space-y-3" data-testid="admin-boost-active">
            {active.map((c) => <BoostCard key={c.id} c={c} />)}
          </div>
        </>
      )}

      {rejected.length > 0 && (
        <>
          <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500 mb-2 mt-5">Rejected</p>
          <div className="space-y-3" data-testid="admin-boost-rejected">
            {rejected.map((c) => <BoostCard key={c.id} c={c} />)}
          </div>
        </>
      )}
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [claims, setClaims] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [gifs, setGifs] = useState([]);
  const [endorsements, setEndorsements] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [top3, setTop3] = useState(null);
  const [boostRequests, setBoostRequests] = useState([]);
  const [freeBoostRequests, setFreeBoostRequests] = useState([]);
  const [boostSubmissions, setBoostSubmissions] = useState([]);
  const [videos, setVideos] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [reasons, setReasons] = useState({});
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [busy, setBusy] = useState("");
  const [activeTab, setActiveTab] = useState("queues");

  const load = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/claims").then((r) => setClaims(r.data.items)).catch(() => {});
    api.get("/admin/companies").then((r) => setCompanies(r.data.items)).catch(() => {});
    api.get("/admin/gifs").then((r) => setGifs(r.data.items)).catch(() => {});
    api.get("/admin/endorsements").then((r) => setEndorsements(r.data.items)).catch(() => {});
    api.get("/admin/reviews").then((r) => setReviews(r.data.items)).catch(() => {});
    api.get("/admin/top3").then((r) => setTop3(r.data)).catch(() => {});
    api.get("/admin/boost-requests").then((r) => setBoostRequests(r.data.items || [])).catch(() => {});
    api.get("/admin/free-boost-requests").then((r) => setFreeBoostRequests(r.data.items || [])).catch(() => {});
    api.get("/admin/boost-submissions").then((r) => setBoostSubmissions(r.data || [])).catch(() => {});
    api.get("/admin/files/videos").then((r) => setVideos(r.data.items || [])).catch(() => {});
    api.get("/admin/users").then((r) => setAllUsers(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && user.role === "admin") load();
  }, [user, load]);

  const act = async (claim, action) => {
    setBusy(claim.id);
    try {
      await api.post(`/admin/claims/${claim.id}/${action}`);
      toast.success(action === "approve" ? `${claim.company_name} is now marked as Claimed.` : "Claim rejected.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const mediaApprove = async (item) => {
    const itemKey = `${item.id}-${item.media_type}`;
    setBusy(itemKey);
    try {
      await api.post(`/admin/${item.media_type === "video" ? "videos" : "gifs"}/${item.id}/approve`);
      toast.success(`${item.name}'s ${item.media_type === "video" ? "video" : "animated logo"} is live.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const mediaReject = async (item) => {
    const itemKey = `${item.id}-${item.media_type}`;
    setBusy(itemKey);
    try {
      await api.post(`/admin/${item.media_type === "video" ? "videos" : "gifs"}/${item.id}/reject`, { reason: reasons[itemKey] || "" });
      toast.success(`${item.name}'s ${item.media_type === "video" ? "video" : "GIF"} rejected.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const verify = async (c) => {
    setBusy(c.id);
    try {
      await api.post(`/admin/companies/${c.id}/verify`, { verified: !c.verified });
      toast.success(c.verified ? `${c.name} unverified.` : `${c.name} is now Verified.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const quickAddBusiness = async (form) => {
    setBusy("quick-add");
    try {
      const { data } = await api.post("/admin/companies/quick-add", form);
      toast.success(`${data.name} added — unclaimed on the leaderboard.`);
      load();
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    } finally {
      setBusy("");
    }
  };

  const deleteVideo = async (v) => {
    if (!window.confirm(`Are you sure you want to delete this video${v.company_name ? ` (${v.company_name})` : ""}? This cannot be undone.`)) return;
    setBusy(v.id);
    try {
      await api.delete(`/admin/files/${v.id}`);
      toast.success("Video deleted.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Are you sure you want to delete ${u.name || u.email}? This cannot be undone.`)) return;
    setBusy(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`${u.name || u.email} deleted.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const assignTop3 = async (companyId, position) => {
    setBusy(`top3-${position}`);
    try {
      const response = await api.post(`/admin/companies/${companyId}/top3`, { position });
      setTop3(response.data);
      toast.success(`Top 3 position #${position} updated.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const removeTop3 = async (companyId) => {
    setBusy("top3-remove");
    try {
      const response = await api.delete(`/admin/companies/${companyId}/top3`);
      setTop3(response.data);
      toast.success("Top 3 slot opened.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };


  const boostApprove = async (c) => {
    setBusy(`boost-${c.id}`);
    try {
      await api.post(`/admin/boost-requests/${c.id}/approve`);
      toast.success(`${c.name}'s Boost is now live on Top Spot.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const boostReject = async (c) => {
    setBusy(`boost-${c.id}`);
    try {
      await api.post(`/admin/boost-requests/${c.id}/reject`);
      toast.success(`${c.name}'s Boost request rejected.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const boostDeactivate = async (c) => {
    setBusy(`boost-${c.id}`);
    try {
      await api.post(`/admin/boost-requests/${c.id}/deactivate`);
      toast.success(`${c.name}'s Boost deactivated.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const saveEdit = async (c) => {
    setBusy(c.id);
    try {
      await api.put(`/admin/companies/${c.id}`, editForm);
      toast.success(`${editForm.name} updated.`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const freeBoostApprove = async (req) => {
    setBusy(req.id);
    try {
      await api.post(`/admin/free-boost-requests/${req.id}/approve`);
      toast.success(`Free boost approved for ${req.company_name}. 3-day trial starts now.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const freeBoostReject = async (req) => {
    setBusy(req.id);
    try {
      await api.post(`/admin/free-boost-requests/${req.id}/reject`);
      toast.success("Request rejected.");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const freeBoostRevoke = async (req) => {
    setBusy(req.id);
    try {
      await api.post(`/admin/free-boost-requests/${req.id}/revoke`);
      toast.success(`Boost revoked for ${req.company_name}.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const boostSubApprove = async (sub) => {
    if (subNeedsListing(sub) && !sub.listing_id) {
      const ok = window.confirm(
        `⚠ "${sub.business_name}" is NOT linked to a business listing.\n\n` +
        `${TIER_LABELS[sub.tier] || sub.tier} needs a linked listing to activate the leaderboard/home-screen video and Top Spot. ` +
        `If you approve now, the submission will be marked approved but nothing will appear on the site.\n\n` +
        `Ask the submitter to claim & link a listing, then resubmit. Approve anyway?`
      );
      if (!ok) return;
    }
    setBusy(sub.id);
    try {
      await api.post(`/admin/boost-submissions/${sub.id}/approve`);
      toast.success(`${sub.business_name}'s submission approved.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const boostSubReject = async (sub, reason) => {
    setBusy(sub.id);
    try {
      await api.post(`/admin/boost-submissions/${sub.id}/reject`, { reason: reason || "" });
      toast.success(`${sub.business_name}'s submission rejected.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const boostSubDeactivate = async (sub) => {
    if (!window.confirm(`Deactivate ${sub.business_name}'s ${TIER_LABELS[sub.tier] || sub.tier} submission? This will undo all activated features.`)) return;
    setBusy(sub.id);
    try {
      await api.post(`/admin/boost-submissions/${sub.id}/deactivate`);
      toast.success(`${sub.business_name}'s submission deactivated.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const boostSubExtend = async (sub) => {
    const tierLabel = TIER_LABELS[sub.tier] || sub.tier;
    if (!window.confirm(`Extend ${sub.business_name}'s ${tierLabel} by its tier duration?${sub.is_expired ? " This will also re-activate it." : ""}`)) return;
    setBusy(sub.id);
    try {
      const { data } = await api.post(`/admin/boost-submissions/${sub.id}/extend`);
      toast.success(`${sub.business_name}'s ${tierLabel} extended by ${data.expiry_days} days.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const boostSubRefund = async (sub) => {
    if (!window.confirm(`Refund ${fmtMoney(sub.amount_paid, sub.currency)} to ${sub.contact_email}? This refunds the Stripe payment and emails the owner.`)) return;
    setBusy(sub.id);
    try {
      await api.post(`/admin/boost-submissions/${sub.id}/refund`);
      toast.success(`Refund issued to ${sub.contact_email}.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const boostSubLinkListing = async (sub, listingId) => {
    setBusy(sub.id);
    try {
      const { data } = await api.post(`/admin/boost-submissions/${sub.id}/link-listing`, { listing_id: listingId });
      toast.success(`Payment linked to ${data.business_name}.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const boostSubDelete = async (sub) => {
    if (!window.confirm(`Permanently delete ${sub.business_name}'s ${TIER_LABELS[sub.tier] || sub.tier} submission? This removes the uploaded video/media and cannot be undone.`)) return;
    setBusy(sub.id);
    try {
      await api.delete(`/admin/boost-submissions/${sub.id}`);
      toast.success(`${sub.business_name}'s submission deleted.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const deleteCompany = async (c) => {
    if (!window.confirm(`Delete ${c.name} from the leaderboard? This cannot be undone.`)) return;
    setBusy(c.id);
    try {
      await api.delete(`/admin/companies/${c.id}`);
      toast.success(`${c.name} deleted.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const removeMedia = async (company, mediaType) => {
    const label = mediaType === "gif" ? "GIF" : mediaType;
    if (!window.confirm(`Remove the uploaded ${label} from ${company.name}? The listing will fall back to its remaining media.`)) return;
    const key = `${company.id}-${mediaType}`;
    setBusy(key);
    try {
      await api.delete(`/admin/companies/${company.id}/media/${mediaType}`);
      toast.success(`${company.name}'s ${label} removed.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const toggleLandingVideo = async (company, enable) => {
    const key = `${company.id}-landing-video`;
    setBusy(key);
    try {
      await api.post(`/admin/companies/${company.id}/landing-video`, { enable });
      toast.success(enable ? `${company.name}'s video is now featured on the landing page.` : `${company.name}'s video removed from the landing page.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const uploadMedia = async (company, mediaType, file) => {
    if (!file) return;
    const key = `upload-${company.id}-${mediaType}`;
    setBusy(key);
    try {
      // Pre-scan before storing
      const scanForm = new FormData();
      scanForm.append("file", file);
      const { data: scan } = await api.post("/admin/scan-file", scanForm, { headers: { "Content-Type": "multipart/form-data" } });
      if (!scan.safe) {
        toast.error(`File blocked: ${scan.reason}`);
        return;
      }
      toast.info(`Scan passed (${scan.reason}) — uploading…`, { duration: 2000 });

      const form = new FormData();
      form.append("file", file);
      await api.post(`/admin/companies/${company.id}/media/${mediaType}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(mediaType === "video" ? `${company.name}'s video is live.` : `${company.name}'s logo updated.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };



  const removeEndorsement = async (e) => {
    setBusy(e.id);
    try {
      await api.delete(`/admin/endorsements/${e.id}`);
      toast.success("Endorsement removed.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  const removeReview = async (review) => {
    setBusy(review.id);
    try {
      await api.delete(`/admin/reviews/${review.id}`);
      toast.success("Review removed.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };


  if (user === null) {
    return (
      <main className="pt-28 md:pt-16 min-h-screen flex items-center justify-center">
        <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-slate-600 animate-pulse">Loading…</p>
      </main>
    );
  }

  if (!user) {
    return (
      <GatePanel
        icon={LockKeyhole}
        title="Admin area"
        body="Sign in with an admin account to review claim requests."
        action={
          <button
            data-testid="admin-signin-cta"
            onClick={() => navigate("/auth", { state: { from: location.pathname } })}
            className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm rounded-full px-8 py-3 transition-colors duration-200"
          >
            Sign in
          </button>
        }
      />
    );
  }

  if (user.role !== "admin") {
    return (
      <GatePanel
        icon={LockKeyhole}
        title="Restricted"
        body={`You're signed in as ${user.email}, which isn't an admin account.`}
        action={
          <Link to="/" data-testid="admin-back-home-link" className="text-emerald-400 hover:underline text-sm">
            ← Back to HighRank
          </Link>
        }
      />
    );
  }

  const pending = (claims || []).filter((c) => c.status === "pending");
  const processed = (claims || []).filter((c) => c.status !== "pending");

  const queueBadge =
    (stats?.pending_claims ?? 0) +
    (stats?.pending_gifs ?? 0) +
    (stats?.pending_free_boosts ?? 0) +
    (stats?.pending_discover_submissions ?? 0) +
    (stats?.pending_boost_top_spot_submissions ?? 0);

  const tabs = [
    { id: "queues",     label: "Review Queues",   badge: queueBadge },
    { id: "boosts",     label: "Active Boosts",   badge: stats?.pending_boosts ?? 0 },
    { id: "listings",   label: "Listings",         badge: null },
    { id: "moderation", label: "Moderation",       badge: null },
  ];

  return (
    <main data-testid="admin-page" className="pt-28 md:pt-16 min-h-screen">
      <Seo title="Admin — HighRank" siteName="HighRank" description="HighRank admin console." />

      {/* ── Header ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-8">
          <FadeUp>
            <p className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-emerald-400 mb-2">{"// Admin console"}</p>
            <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-wide text-slate-100 flex items-center gap-4">
              <ShieldCheck size={40} className="text-emerald-400" /> Control room
            </h1>
          </FadeUp>

          {/* Stats row */}
          <FadeUp delay={0.1}>
            <div data-testid="admin-stats" className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {/* Pending action cards */}
              {[
                { v: stats?.pending_claims ?? "—",                    label: "Claims",           icon: <MailWarning size={14} />, hot: true },
                { v: stats?.pending_gifs ?? "—",                      label: "Media Queue",      icon: <Film size={14} />,        hot: true },
                { v: (stats?.pending_discover_submissions ?? 0) + (stats?.pending_boost_top_spot_submissions ?? 0), label: "Submissions", icon: <Zap size={14} />, hot: true },
                { v: stats?.pending_free_boosts ?? "—",               label: "Free Boost Reqs",  icon: <Rocket size={14} />,      hot: true },
              ].map(({ v, label, icon, hot }) => {
                const active = hot && v !== "—" && v > 0;
                return (
                  <div key={label} className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${active ? "border-red-500/40 bg-red-500/[0.07]" : "border-white/[0.07] bg-white/[0.03]"}`}>
                    <span className={active ? "text-red-400" : "text-slate-600"}>{icon}</span>
                    <div>
                      <p className={`font-mono2 text-lg font-bold leading-none ${active ? "text-red-400" : "text-slate-400"}`}>{v}</p>
                      <p className="font-mono2 text-[9px] uppercase tracking-widest text-slate-600 mt-0.5">{label}</p>
                    </div>
                    {active && <span className="ml-auto w-2 h-2 rounded-full bg-red-400 animate-pulse" />}
                  </div>
                );
              })}
            </div>
            {/* Secondary counters */}
            <div className="mt-3 flex flex-wrap gap-2">
              {[
                [stats?.companies ?? "—", "Companies"],
                [stats?.claimed ?? "—", "Claimed"],
                [stats?.verified ?? "—", "Verified"],
                [stats?.users ?? "—", "Users"],
                [stats?.upvotes ?? "—", "Endorsements"],
                [stats?.reviews ?? "—", "Reviews"],
                [stats?.total_views ?? "—", "Views"],
              ].map(([v, label]) => (
                <div key={label} className="inline-flex items-center gap-1.5 rounded-lg border border-white/[0.07] bg-white/[0.03] px-3 py-1.5">
                  <span className="font-mono2 text-sm font-bold text-slate-300 leading-none">{v}</span>
                  <span className="font-mono2 text-[9px] uppercase tracking-[0.18em] text-slate-600 leading-none">{label}</span>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ── Tab bar ─────────────────────────────────────────────── */}
      <div className="sticky top-0 z-30 border-b border-slate-200 dark:border-white/[0.07] bg-white/95 dark:bg-[#0D0D0B]/95 backdrop-blur-md shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setActiveTab(t.id)}
              className={`relative shrink-0 flex items-center gap-2 px-4 py-3.5 font-mono2 text-[11px] uppercase tracking-widest transition-colors border-b-2 ${
                activeTab === t.id
                  ? "border-emerald-500 text-emerald-600 dark:text-emerald-400 dark:border-emerald-400"
                  : "border-transparent text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
              }`}
            >
              {t.label}
              {t.badge > 0 && (
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold leading-none">
                  {t.badge > 99 ? "99+" : t.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab content ─────────────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-8 pb-24 space-y-10">

        {/* TAB 1 — Review Queues */}
        {activeTab === "queues" && (
          <>
            {/* Pending Business Claims */}
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-8 rounded-lg bg-red-500/15 flex items-center justify-center">
                  <MailWarning size={16} className="text-red-400" />
                </div>
                <div>
                  <h2 className="font-display text-lg uppercase tracking-wide text-slate-100">Business Claims</h2>
                  <p className="text-xs text-slate-500">OTP-verified ownership requests — approve or reject.</p>
                </div>
                <span className="ml-auto font-mono2 text-[11px] text-slate-500">{pending.length} pending</span>
              </div>
              <div className="space-y-3" data-testid="admin-pending-claims">
                {pending.length === 0 ? (
                  <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-8 text-center text-sm text-slate-600">
                    No pending claims. Inbox zero.
                  </div>
                ) : pending.map((c) => <ClaimCard key={c.id} claim={c} busy={busy} onAct={act} />)}
              </div>
            </div>

            <UnmatchedPaymentsManager
              submissions={boostSubmissions.filter((s) => s.is_paid && s.match_status === "unmatched" && s.refund_status !== "refunded")}
              companies={companies}
              busy={busy}
              onLink={boostSubLinkListing}
            />

            <DiscoverSubmissionsManager
              submissions={boostSubmissions.filter((s) => s.review_queue === "discover")}
              busy={busy}
              onApprove={boostSubApprove}
              onReject={boostSubReject}
              onDeactivate={boostSubDeactivate}
              onExtend={boostSubExtend}
              onDelete={boostSubDelete}
              onRefund={boostSubRefund}
            />

            <BoostTopSpotSubmissionsManager
              submissions={boostSubmissions.filter((s) => s.review_queue === "boost_top_spot")}
              busy={busy}
              onApprove={boostSubApprove}
              onReject={boostSubReject}
              onDeactivate={boostSubDeactivate}
              onExtend={boostSubExtend}
              onDelete={boostSubDelete}
              onRefund={boostSubRefund}
            />

            <FreeBoostRequestsManager
              requests={freeBoostRequests}
              busy={busy}
              onApprove={freeBoostApprove}
              onReject={freeBoostReject}
              onRevoke={freeBoostRevoke}
            />

            <GifQueue gifs={gifs} reasons={reasons} setReasons={setReasons} busy={busy} onApprove={mediaApprove} onReject={mediaReject} />
          </>
        )}

        {/* TAB 2 — Active Boosts */}
        {activeTab === "boosts" && (
          <BoostRequestsManager
            boostRequests={boostRequests}
            busy={busy}
            onApprove={boostApprove}
            onReject={boostReject}
            onDeactivate={boostDeactivate}
          />
        )}

        {/* TAB 3 — Listings */}
        {activeTab === "listings" && (
          <>
            <QuickAddBusiness busy={busy} onAdd={quickAddBusiness} />
            <Top3Manager data={top3} busy={busy} onAssign={assignTop3} onRemove={removeTop3} />
            <ListingsManager
              companies={companies}
              editing={editing}
              setEditing={setEditing}
              editForm={editForm}
              setEditForm={setEditForm}
              busy={busy}
              onVerify={verify}
              onSave={saveEdit}
              onDelete={deleteCompany}
              onRemoveMedia={removeMedia}
              onUploadMedia={uploadMedia}
              onToggleLandingVideo={toggleLandingVideo}
            />
            <VideosCleanup videos={videos} busy={busy} onDelete={deleteVideo} />
            <UsersCleanup users={allUsers} busy={busy} onDelete={deleteUser} />
          </>
        )}

        {/* TAB 4 — Moderation */}
        {activeTab === "moderation" && (
          <>
            <EndorsementsModeration endorsements={endorsements} busy={busy} onRemove={removeEndorsement} />
            <ReviewsModeration reviews={reviews} busy={busy} onRemove={removeReview} />
            {processed.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 rounded-lg bg-slate-500/15 flex items-center justify-center">
                    <Check size={16} className="text-slate-400" />
                  </div>
                  <h2 className="font-display text-lg uppercase tracking-wide text-slate-100">
                    Processed Claims
                    <span className="ml-2 font-mono2 text-sm text-slate-500">({processed.length})</span>
                  </h2>
                </div>
                <div className="space-y-3" data-testid="admin-processed-claims">
                  {processed.map((c) => <ClaimCard key={c.id} claim={c} busy={busy} onAct={act} />)}
                </div>
              </div>
            )}
          </>
        )}

      </section>
    </main>
  );
}


const STATUS_STYLES = {
  pending:        "bg-amber-500/10 border-amber-500/30 text-amber-400",
  pending_review: "bg-amber-500/10 border-amber-500/30 text-amber-400",
  awaiting_content: "bg-sky-500/10 border-sky-500/30 text-sky-400",
  approved:       "bg-emerald-500/10 border-emerald-500/30 text-emerald-400",
  rejected:       "bg-red-500/10 border-red-500/30 text-red-400",
  revoked:        "bg-slate-500/10 border-slate-500/30 text-slate-400",
  expired:        "bg-slate-500/10 border-slate-500/30 text-slate-500",
};

function FreeBoostRequestsManager({ requests, busy, onApprove, onReject, onRevoke }) {
  return (
    <section data-testid="free-boost-requests-section" className="mt-12">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center">
          <Rocket size={16} className="text-amber-400" />
        </div>
        <div>
          <h2 className="font-display text-lg uppercase tracking-wide text-slate-100">Free Boost Requests</h2>
          <p className="text-xs text-slate-500">3-day free trial — approve to activate, revoke to remove immediately.</p>
        </div>
        <span className="ml-auto font-mono2 text-[11px] text-slate-500">{requests.filter((r) => r.status === "pending").length} pending</span>
      </div>

      {requests.length === 0 ? (
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-8 text-center text-sm text-slate-600">
          No free boost requests yet.
        </div>
      ) : (
        <div className="rounded-xl border border-white/[0.06] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.06] bg-white/[0.03]">
                {["Business", "Category", "Region", "Requested", "Boost Period", "Status", "Actions"].map((h) => (
                  <th key={h} className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500 text-left px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <tr key={req.id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition">
                  <td className="px-4 py-3">
                    <a href={`/company/${req.company_slug}`} target="_blank" rel="noreferrer" className="font-medium text-slate-200 hover:text-emerald-400 transition">
                      {req.company_name}
                    </a>
                    <p className="text-xs text-slate-600 truncate max-w-[140px]">{req.requester_email}</p>
                  </td>
                  <td className="px-4 py-3 text-slate-400">{req.category || "—"}</td>
                  <td className="px-4 py-3 text-slate-400">{req.region || "—"}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{req.requested_at ? new Date(req.requested_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {req.boost_start_date ? (
                      <span>{new Date(req.boost_start_date).toLocaleDateString()} → {new Date(req.boost_end_date).toLocaleDateString()}</span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 border rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${STATUS_STYLES[req.status] || STATUS_STYLES.pending}`}>
                      {req.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {req.status === "pending" && (
                        <>
                          <button
                            data-testid={`free-boost-approve-${req.id}`}
                            disabled={busy === req.id}
                            onClick={() => onApprove(req)}
                            className="inline-flex items-center gap-1 border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-bold rounded-full px-3 py-1.5 transition disabled:opacity-50"
                          >
                            <Check size={11} /> Approve
                          </button>
                          <button
                            data-testid={`free-boost-reject-${req.id}`}
                            disabled={busy === req.id}
                            onClick={() => onReject(req)}
                            className="inline-flex items-center gap-1 border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-bold rounded-full px-3 py-1.5 transition disabled:opacity-50"
                          >
                            <X size={11} /> Reject
                          </button>
                        </>
                      )}
                      {req.status === "approved" && (
                        <button
                          data-testid={`free-boost-revoke-${req.id}`}
                          disabled={busy === req.id}
                          onClick={() => onRevoke(req)}
                          className="inline-flex items-center gap-1 border border-slate-500/30 bg-slate-500/10 hover:bg-slate-500/20 text-slate-400 text-xs font-bold rounded-full px-3 py-1.5 transition disabled:opacity-50"
                        >
                          <X size={11} /> Revoke
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}


const TIER_LABELS = { discover_boost: "Discover Boost", boost: "Boost", pro_boost: "Pro Boost" };
const OPT_LABELS  = { option_a: "Option A — Discover Slot", option_b: "Option B — Leaderboard Video" };

// Shared table used by both queue views
function SubmissionsTable({ submissions, busy, onApprove, onReject, onDeactivate, onExtend, onDelete, onRefund, emptyText }) {
  const [rejectReasons, setRejectReasons] = useState({});

  if (submissions.length === 0) {
    return (
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-8 text-center text-sm text-slate-600">
        {emptyText}
      </div>
    );
  }

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;
  const isExpiringSoon = (iso) => {
    if (!iso) return false;
    return (new Date(iso) - Date.now()) < 3 * 24 * 60 * 60 * 1000; // < 3 days
  };

  return (
    <div className="rounded-xl border border-white/[0.06] overflow-x-auto">
      <table className="w-full text-sm min-w-[700px]">
        <thead>
          <tr className="border-b border-white/[0.06] bg-white/[0.03]">
            {["Business", "Tier / Option", "Social Link", "Video", "Submitted", "Status / Expiry", "Actions"].map((h) => (
              <th key={h} className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500 text-left px-4 py-3">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {submissions.map((sub) => (
            <tr
              key={sub.id}
              data-testid={`boost-sub-row-${sub.id}`}
              className={`border-b border-white/[0.04] transition ${
                sub.is_expired ? "bg-red-500/[0.06] hover:bg-red-500/[0.1]" : "hover:bg-white/[0.02]"
              }`}
            >
              <td className="px-4 py-3">
                <p className="font-medium text-slate-200">{sub.business_name}</p>
                <p className="text-xs text-slate-600 truncate max-w-[140px]">{sub.contact_email}</p>
                {sub.is_paid && (
                  <span
                    data-testid={`boost-sub-paid-${sub.id}`}
                    title={`Paid ${fmtMoney(sub.amount_paid, sub.currency)}${sub.paid_at ? ` on ${new Date(sub.paid_at).toLocaleDateString()}` : ""}`}
                    className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold text-emerald-400"
                  >
                    <CreditCard size={10} /> PAID {fmtMoney(sub.amount_paid, sub.currency)}
                  </span>
                )}
                {sub.is_paid && sub.paid_at && (
                  <p className="text-[10px] text-slate-600 mt-1">Paid {new Date(sub.paid_at).toLocaleDateString()}</p>
                )}
                {sub.is_paid && sub.match_status === "unmatched" && (
                  <span
                    data-testid={`boost-sub-unmatched-${sub.id}`}
                    title="Payment received but no listing matched — link it from the Unmatched Payments panel."
                    className="mt-1 inline-flex items-center gap-1 rounded-full border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-[10px] font-semibold text-red-400"
                  >
                    <AlertTriangle size={10} /> Unmatched payment
                  </span>
                )}
                {sub.stripe_url && (
                  <a
                    href={sub.stripe_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`boost-sub-stripe-${sub.id}`}
                    className="block text-[10px] text-sky-400 hover:underline mt-1"
                  >
                    View payment in Stripe →
                  </a>
                )}
                {subNeedsListing(sub) && !sub.listing_id && (
                  <span
                    data-testid={`boost-sub-unlinked-${sub.id}`}
                    title="This submission is not linked to a business listing. Its video / Top Spot cannot be activated until a listing is linked."
                    className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold text-amber-400"
                  >
                    <AlertTriangle size={10} /> Not linked to a listing
                  </span>
                )}
              </td>
              <td className="px-4 py-3">
                <span className="text-slate-300 text-xs font-semibold">{TIER_LABELS[sub.tier] || sub.tier}</span>
                {sub.option_selected && (
                  <p className="text-[11px] text-slate-500 mt-0.5">{OPT_LABELS[sub.option_selected] || sub.option_selected}</p>
                )}
              </td>
              <td className="px-4 py-3">
                {sub.social_link ? (
                  <a href={safeUrl(sub.social_link)} target="_blank" rel="noreferrer"
                    className="text-emerald-400 hover:underline text-xs truncate max-w-[120px] block">
                    View post →
                  </a>
                ) : <span className="text-slate-600 text-xs">—</span>}
              </td>
              <td className="px-4 py-3">
                {sub.video_url ? (
                  <div className="space-y-1">
                    <video src={sub.video_url} controls
                      className="w-24 h-14 rounded-lg object-cover border border-white/10" />
                    {sub.thumbnail_url && sub.thumbnail_url !== sub.video_url && (
                      <img src={sub.thumbnail_url} alt="thumbnail"
                        className="w-24 h-14 rounded-lg object-cover border border-white/10" />
                    )}
                  </div>
                ) : sub.thumbnail_url ? (
                  <img src={sub.thumbnail_url} alt="thumbnail"
                    className="w-24 h-14 rounded-lg object-cover border border-white/10" />
                ) : <span className="text-slate-600 text-xs">—</span>}
              </td>
              <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                {sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString() : "—"}
              </td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center gap-1 border rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLES[sub.status] || STATUS_STYLES.pending}`}>
                  {sub.status === "pending_review" ? "Pending" : sub.status === "awaiting_content" ? "Awaiting content" : sub.status}
                </span>
                {/* Expiry info for approved submissions */}
                {sub.status === "approved" && sub.expires_at && (
                  <p className={`text-[10px] mt-1 ${isExpiringSoon(sub.expires_at) ? "text-amber-400" : "text-slate-600"}`}>
                    Expires {fmtDate(sub.expires_at)}{sub.days_left != null ? ` (${sub.days_left}d left)` : ""}
                    {sub.expiry_days && <span className="text-slate-700"> · {sub.expiry_days}d tier</span>}
                    {isExpiringSoon(sub.expires_at) && " ⚠"}
                  </p>
                )}
                {sub.status === "approved" && sub.renewal_reminder_sent_at && (
                  <p data-testid={`boost-sub-reminder-${sub.id}`} className="text-[10px] text-sky-400 mt-1">Renewal reminder sent</p>
                )}
                {sub.is_expired && (
                  <span
                    data-testid={`boost-sub-expired-${sub.id}`}
                    title="This boost ran out and was switched off automatically. Delete it to clean up its video."
                    className="mt-1 inline-flex items-center gap-1 rounded-full border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-[10px] font-semibold text-red-400"
                  >
                    <AlertTriangle size={10} /> Expired — clean up
                  </span>
                )}
                {sub.status === "deactivated" && sub.deactivated_at && (
                  <p className="text-[10px] text-slate-600 mt-1">Ended {fmtDate(sub.deactivated_at)}</p>
                )}
                {sub.video_missing && (
                  <p data-testid={`boost-sub-video-missing-${sub.id}`} className="text-[10px] text-amber-400 mt-1">Video file deleted</p>
                )}
                {sub.status === "approved" && sub.needs_video && (
                  <p data-testid={`boost-sub-needs-video-${sub.id}`} className="text-[10px] text-amber-400 mt-1">No video uploaded</p>
                )}
                {sub.placement_issues && sub.placement_issues.length > 0 && (
                  <div className="mt-1.5 space-y-0.5">
                    {sub.placement_issues.map((issue, i) => (
                      <p key={i} data-testid={`boost-sub-issue-${sub.id}-${i}`}
                        className="flex items-start gap-1 text-[10px] text-red-400 font-semibold leading-snug">
                        <AlertTriangle size={10} className="shrink-0 mt-px" /> {issue}
                      </p>
                    ))}
                  </div>
                )}
                {sub.admin_note && (
                  <p className="text-[10px] text-slate-600 mt-1 max-w-[120px] truncate">{sub.admin_note}</p>
                )}
              </td>
              <td className="px-4 py-3">
                {sub.status === "pending_review" && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`boost-sub-approve-${sub.id}`}
                        disabled={busy === sub.id}
                        onClick={() => onApprove(sub)}
                        className="inline-flex items-center gap-1 border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-bold rounded-full px-3 py-1.5 transition disabled:opacity-50"
                      >
                        <Check size={11} /> Approve
                      </button>
                      <button
                        data-testid={`boost-sub-reject-${sub.id}`}
                        disabled={busy === sub.id}
                        onClick={() => onReject(sub, rejectReasons[sub.id])}
                        className="inline-flex items-center gap-1 border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-bold rounded-full px-3 py-1.5 transition disabled:opacity-50"
                      >
                        <X size={11} /> Reject
                      </button>
                    </div>
                    <input
                      type="text"
                      placeholder="Rejection reason (optional)"
                      value={rejectReasons[sub.id] || ""}
                      onChange={(e) => setRejectReasons((r) => ({ ...r, [sub.id]: e.target.value }))}
                      className="w-full text-[11px] rounded-lg border border-white/10 bg-[#121210] px-2 py-1.5 text-slate-400 placeholder:text-slate-600 outline-none"
                    />
                  </div>
                )}
                {sub.status === "approved" && (
                  <div className="flex items-center gap-2">
                    {onExtend && (
                      <button
                        data-testid={`boost-sub-extend-${sub.id}`}
                        disabled={busy === sub.id}
                        onClick={() => onExtend(sub)}
                        title={`Extend by ${sub.expiry_days || "tier"} days`}
                        className="inline-flex items-center gap-1 border border-sky-500/30 bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 text-xs font-bold rounded-full px-3 py-1.5 transition disabled:opacity-50"
                      >
                        <Clock size={11} /> Extend
                      </button>
                    )}
                    {onDeactivate && (
                      <button
                        data-testid={`boost-sub-deactivate-${sub.id}`}
                        disabled={busy === sub.id}
                        onClick={() => onDeactivate(sub)}
                        className="inline-flex items-center gap-1 border border-orange-500/30 bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 text-xs font-bold rounded-full px-3 py-1.5 transition disabled:opacity-50"
                      >
                        <X size={11} /> Deactivate
                      </button>
                    )}
                  </div>
                )}
                {sub.is_expired && onExtend && (
                  <button
                    data-testid={`boost-sub-reactivate-${sub.id}`}
                    disabled={busy === sub.id}
                    onClick={() => onExtend(sub)}
                    title="Re-activate and extend by tier duration"
                    className="inline-flex items-center gap-1 border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-bold rounded-full px-3 py-1.5 transition disabled:opacity-50"
                  >
                    <Clock size={11} /> Re-activate
                  </button>
                )}
                {sub.is_paid && onRefund && sub.refund_status !== "refunded" && (
                  sub.refund_status === "refunded" ? (
                    <span
                      data-testid={`boost-sub-refunded-${sub.id}`}
                      className="inline-flex items-center gap-1 rounded-full border border-slate-500/30 bg-slate-500/10 px-3 py-1.5 text-xs font-bold text-slate-400"
                    >
                      <Check size={11} /> Refunded
                    </span>
                  ) : (
                    <button
                      data-testid={`boost-sub-refund-${sub.id}`}
                      disabled={busy === sub.id}
                      onClick={() => onRefund(sub)}
                      title="Refund this payment in Stripe and email the owner"
                      className="inline-flex items-center gap-1 border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 text-xs font-bold rounded-full px-3 py-1.5 transition disabled:opacity-50"
                    >
                      <CreditCard size={11} /> Issue Refund
                    </button>
                  )
                )}
                {onDelete && (
                  <button
                    data-testid={`boost-sub-delete-${sub.id}`}
                    disabled={busy === sub.id}
                    onClick={() => onDelete(sub)}
                    title="Permanently delete this submission and its uploaded video"
                    className="mt-2 inline-flex items-center gap-1 border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-bold rounded-full px-3 py-1.5 transition disabled:opacity-50"
                  >
                    <Trash2 size={11} /> Delete
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Queue 1: Discover Admin — Tier 1 Option A only ────────────────
function DiscoverSubmissionsManager({ submissions, busy, onApprove, onReject, onDeactivate, onExtend, onDelete, onRefund }) {
  return (
    <section data-testid="discover-submissions-section" className="mt-12">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center">
          <Compass size={16} className="text-emerald-400" />
        </div>
        <div>
          <h2 className="font-display text-lg uppercase tracking-wide text-slate-100">Discover Submissions</h2>
          <p className="text-xs text-slate-500">
            Discover Boost — Option A (Sponsored Discover page slot + business product video).
          </p>
        </div>
        <span className="ml-auto font-mono2 text-[11px] text-slate-500">
          {submissions.filter((s) => s.status === "pending_review").length} pending
        </span>
      </div>
      <SubmissionsTable
        submissions={submissions}
        busy={busy}
        onApprove={onApprove}
        onReject={onReject}
        onDeactivate={onDeactivate}
        onExtend={onExtend}
        onDelete={onDelete}
        onRefund={onRefund}
        emptyText="No Discover slot submissions yet."
      />
    </section>
  );
}

// ── Queue 2: Boost & Top Spot Admin — all other tiers/options ────
function BoostTopSpotSubmissionsManager({ submissions, busy, onApprove, onReject, onDeactivate, onExtend, onDelete, onRefund }) {
  return (
    <section data-testid="boost-topspot-submissions-section" className="mt-12">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center">
          <Zap size={16} className="text-amber-400" />
        </div>
        <div>
          <h2 className="font-display text-lg uppercase tracking-wide text-slate-100">Boost & Top Spot Submissions</h2>
          <p className="text-xs text-slate-500">
            Discover Boost Option B · Boost ($9.99/10 days) · Pro Boost ($14.99/10 days) — leaderboard, Top Spot, home screen, share card.
          </p>
        </div>
        <span className="ml-auto font-mono2 text-[11px] text-slate-500">
          {submissions.filter((s) => s.status === "pending_review").length} pending
        </span>
      </div>
      <SubmissionsTable
        submissions={submissions}
        busy={busy}
        onApprove={onApprove}
        onReject={onReject}
        onDeactivate={onDeactivate}
        onExtend={onExtend}
        onDelete={onDelete}
        onRefund={onRefund}
        emptyText="No Boost / Top Spot submissions yet."
      />
    </section>
  );
}

